<template>
	<div class="flex flex-col">
		<div
			v-for="log in paginatedLogs"
			:key="log.id"
			class="flex gap-1 sm:gap-5 py-2 flex-row items-center odd:bg-white even:bg-sky-50 px-5"
			:class="{
				'odd:bg-yellow-50 even:bg-yellow-100': log.status?.slug == 'event',
				'odd:bg-red-50 even:bg-red-100': log.status?.slug == 'claim',
				'odd:bg-white even:bg-sky-50': log.status?.slug != 'claim' || log.status?.slug == 'event',
			}">
			<div class="flex flex-col flex-grow break-all">
				<div class="w-full flex font-medium flex-col">
					{{ log?.status?.name }}
					<p class="font-normal" v-if="log?.status?.finishable">
						{{
							log?.status.finishable.first_name + ' ' + log?.status.finishable.last_name + ' ' + log?.status.finishable?.targeted ||
							log.status.finishable.name
						}}
					</p>
					<p class="font-normal" v-if="log.linkable">
						{{ log.linkable.name }}
					</p>
					<div>
						<log-comment class="text-gray-500 text-sm" v-if="log.comment" :log="log" />
					</div>
				</div>
			</div>
			<div class="flex flex-col items-center text-xs">
				<log-date class="text-gray-500" :log="log" />
				<div class="text-gray-400 text-center">
					<p>{{ log?.user?.first_name.charAt(0) }}. {{ log?.user?.last_name }}</p>
				</div>
			</div>
			<div
				v-if="log?.user?.id == page.props.auth.user.id || page.props.auth.user.role == 'admin'"
				@click="deleteLog(log?.id)"
				class="text-slate-500 hover:text-slate-700 cursor-pointer">
				<i class="fas fa-trash"></i>
			</div>
		</div>
	</div>
	<div v-if="totalPages > 1" class="flex items-center justify-center gap-2 mt-3">
		<button
			:disabled="currentPage <= 1"
			@click="currentPage = Math.max(1, currentPage - 1)"
			class="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
			Préc
		</button>
		<button
			v-for="p in totalPages"
			:key="p"
			@click="currentPage = p"
			:class="['px-3 py-1 rounded', { 'bg-blue-500 text-white': currentPage === p, 'bg-gray-100 hover:bg-gray-200': currentPage !== p }]">
			{{ p }}
		</button>
		<button
			:disabled="currentPage >= totalPages"
			@click="currentPage = Math.min(totalPages, currentPage + 1)"
			class="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
			Suiv
		</button>
	</div>
</template>

<script setup>
	import { ref, computed } from 'vue';
	import { usePage, router } from '@inertiajs/vue3';
	import { toast } from 'vue3-toastify';
	import Swal from 'sweetalert2';

	const page = usePage();

	const props = defineProps({
		logs: {
			type: [Array, Object],
			required: true,
			default: () => [],
		},
	});

	// Pagination (client-side): keep max 15 logs per page
	const currentPage = ref(1);
	const perPage = 15;

	const normalizedLogs = computed(() => {
		if (Array.isArray(props.logs)) return props.logs;
		if (props.logs && Array.isArray(props.logs.data)) return props.logs.data;
		return [];
	});

	const totalPages = computed(() => Math.max(1, Math.ceil(normalizedLogs.value.length / perPage)));

	const paginatedLogs = computed(() => {
		const start = (currentPage.value - 1) * perPage;
		return normalizedLogs.value.slice(start, start + perPage);
	});

	const deleteLog = (logId) => {
		Swal.fire({
			title: 'Êtes-vous sûr ?',
			text: 'Cette action est irréversible.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Oui, supprimer',
			cancelButtonText: 'Annuler',
		}).then((result) => {
			if (result.isConfirmed) {
				router.delete(route('log.deleteComment', { log: logId }), {
					onSuccess: () => {
						toast.success('Le commentaire a bien été supprimé');
					},
					onError: () => {
						toast.error('Une erreur est survenue');
					},
				});
			}
		});
	};
</script>
